import { cn } from '@/lib/utils';
import { omit } from 'lodash';
import { useContext, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { PluggableList } from 'react-markdown/lib';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { visit } from 'unist-util-visit';

import { ChainlitContext, type IMessageElement } from '@chainlit/react-client';

import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card } from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';

import BlinkingCursor from './BlinkingCursor';
import CodeSnippet from './CodeSnippet';
import { DMSourceBadge } from './DMSourceBadge';
import { DMSourceGroupBadge } from './DMSourceGroupBadge';
import { ElementRef } from './Elements/ElementRef';
import {
    type AlertProps,
    MarkdownAlert,
    alertComponents,
    normalizeAlertType
} from './MarkdownAlert';
import { TableWithExport } from './TableWithExport';

interface Props {
    allowHtml?: boolean;
    latex?: boolean;
    refElements?: IMessageElement[];
    children: string;
    className?: string;
    disableSourceBadge?: boolean;
}

const cursorPlugin = () => {
    return (tree: any) => {
        visit(tree, 'text', (node: any, index, parent) => {
            const placeholderPattern = /\u200B/g;
            const matches = [
                ...(node.value?.matchAll(placeholderPattern) || [])
            ];

            if (matches.length > 0) {
                const newNodes: any[] = [];
                let lastIndex = 0;

                matches.forEach((match) => {
                    const [fullMatch] = match;
                    const startIndex = match.index!;
                    const endIndex = startIndex + fullMatch.length;

                    if (startIndex > lastIndex) {
                        newNodes.push({
                            type: 'text',
                            value: node.value!.slice(lastIndex, startIndex)
                        });
                    }

                    newNodes.push({
                        type: 'blinkingCursor',
                        data: {
                            hName: 'blinkingCursor',
                            hProperties: { text: 'Blinking Cursor' }
                        }
                    });

                    lastIndex = endIndex;
                });

                if (lastIndex < node.value!.length) {
                    newNodes.push({
                        type: 'text',
                        value: node.value!.slice(lastIndex)
                    });
                }

                parent!.children.splice(index, 1, ...newNodes);
            }
        });
    };
};

/**
 * Replaces [SourceName] tokens in text nodes with sourceBadge hast nodes,
 * then merges consecutive badges (separated only by whitespace) into a
 * single sourceGroup node so the UI can render one consolidated pill.
 */
const sourceBadgePlugin = () => {
    return (tree: any) => {
        // Pass 1 — expand [Name] tokens into sourceBadge nodes
        visit(tree, 'text', (node: any, index, parent) => {
            if (!parent || index === undefined) return;
            if (parent.type === 'code' || parent.type === 'inlineCode') return;

            const pattern = /\[([^\]\[\n]{1,40})\]/g;
            const matches = [...(node.value?.matchAll(pattern) || [])];
            if (!matches.length) return;

            const newNodes: any[] = [];
            let lastIndex = 0;

            matches.forEach((match) => {
                const startIndex = match.index!;
                const endIndex = startIndex + match[0].length;
                if (startIndex > lastIndex) {
                    newNodes.push({
                        type: 'text',
                        value: node.value.slice(lastIndex, startIndex)
                    });
                }
                newNodes.push({
                    type: 'sourceBadge',
                    data: {
                        hName: 'sourceBadge',
                        hProperties: { name: match[1] }
                    }
                });
                lastIndex = endIndex;
            });

            if (lastIndex < node.value.length) {
                newNodes.push({
                    type: 'text',
                    value: node.value.slice(lastIndex)
                });
            }

            parent.children.splice(index, 1, ...newNodes);
        });

        // Pass 2 — merge consecutive sourceBadge nodes into sourceGroup
        visit(tree, (node: any) => {
            if (!node.children) return;
            const merged: any[] = [];
            let group: string[] = [];

            const flushGroup = () => {
                if (group.length === 0) return;
                merged.push({
                    type: 'sourceGroup',
                    data: {
                        hName: 'sourceGroup',
                        hProperties: { names: JSON.stringify(group) }
                    }
                });
                group = [];
            };

            for (const child of node.children) {
                if (child.type === 'sourceBadge') {
                    group.push(child.data.hProperties.name);
                } else if (
                    child.type === 'text' &&
                    child.value.trim() === '' &&
                    group.length > 0
                ) {
                    // whitespace between badges — keep collecting
                } else {
                    flushGroup();
                    merged.push(child);
                }
            }
            flushGroup();
            node.children = merged;
        });
    };
};

const Markdown = ({
    allowHtml,
    latex,
    refElements,
    className,
    children,
    disableSourceBadge = false
}: Props) => {
    const apiClient = useContext(ChainlitContext);

    const sourceNames = useMemo(() => {
        if (disableSourceBadge) return [];
        const pattern = /\[([^\]\[\n]{1,40})\]/g;
        const matches = [...(children?.matchAll(pattern) || [])];
        return [...new Set(matches.map((m) => m[1]))];
    }, [children, disableSourceBadge]);

    const rehypePlugins = useMemo(() => {
        let rehypePlugins: PluggableList = [];
        if (allowHtml) {
            rehypePlugins = [rehypeRaw as any, ...rehypePlugins];
        }
        if (latex) {
            rehypePlugins = [rehypeKatex as any, ...rehypePlugins];
        }
        return rehypePlugins;
    }, [allowHtml, latex]);

    const remarkPlugins = useMemo(() => {
        let remarkPlugins: PluggableList = [
            cursorPlugin,
            remarkGfm as any,
            remarkDirective as any,
            MarkdownAlert,
            ...(disableSourceBadge ? [] : [sourceBadgePlugin])
        ];

        if (latex) {
            remarkPlugins = [...remarkPlugins, remarkMath as any];
        }
        return remarkPlugins;
    }, [latex, disableSourceBadge]);

    return (
        <ReactMarkdown
            className={cn('prose prose-sm', className)}
            remarkPlugins={remarkPlugins}
            rehypePlugins={rehypePlugins}
            components={{
                ...alertComponents, // add alert components
                sourceBadge({ name }: any) {
                    return <DMSourceBadge name={name} />;
                },
                sourceGroup({ names }: any) {
                    const parsed: string[] =
                        typeof names === 'string' ? JSON.parse(names) : names;
                    return parsed.length === 1 ? (
                        <DMSourceBadge name={parsed[0]} />
                    ) : (
                        <DMSourceGroupBadge names={parsed} />
                    );
                },
                code(props) {
                    return (
                        <code
                            {...omit(props, ['node'])}
                            className="relative bg-muted px-[0.3rem] py-[0.2rem] rounded font-mono font-semibold text-sm"
                        />
                    );
                },
                pre({ children, ...props }: any) {
                    return <CodeSnippet {...props} />;
                },
                a({ children, ...props }) {
                    const name = children as string;
                    const element = refElements?.find((e) => e.name === name);
                    if (element) {
                        return <ElementRef element={element} />;
                    } else {
                        return (
                            <a
                                {...props}
                                className="text-foreground hover:underline"
                                target="_blank"
                            >
                                {children}
                            </a>
                        );
                    }
                },
                img: (image: any) => {
                    return (
                        <div className="sm:max-w-sm md:max-w-md">
                            <AspectRatio
                                ratio={16 / 9}
                                className="bg-muted rounded-md overflow-hidden"
                            >
                                <img
                                    src={
                                        image.src.startsWith('/public')
                                            ? apiClient.buildEndpoint(image.src)
                                            : image.src
                                    }
                                    alt={image.alt}
                                    className="w-full h-full object-contain"
                                />
                            </AspectRatio>
                        </div>
                    );
                },
                blockquote(props) {
                    return (
                        <blockquote
                            {...omit(props, ['node'])}
                            className="mt-6 pl-6 border-l-2 italic"
                        />
                    );
                },
                em(props) {
                    return (
                        <span {...omit(props, ['node'])} className="italic" />
                    );
                },
                strong(props) {
                    return (
                        <span
                            {...omit(props, ['node'])}
                            className="font-medium"
                        />
                    );
                },
                hr() {
                    return <div className="my-4 h-0" aria-hidden="true" />;
                },
                ul(props) {
                    return (
                        <ul
                            {...omit(props, ['node'])}
                            className="my-3 [&>li]:mt-1 ml-3 pl-2 list-disc"
                        />
                    );
                },
                ol(props) {
                    return (
                        <ol
                            {...omit(props, ['node'])}
                            className="my-3 [&>li]:mt-1 ml-3 pl-2 list-decimal"
                        />
                    );
                },
                h1(props) {
                    return (
                        <h1
                            {...omit(props, ['node'])}
                            className="mt-8 first:mt-0 font-medium text-4xl tracking-tight scroll-m-20"
                        />
                    );
                },
                h2(props) {
                    return (
                        <h2
                            {...omit(props, ['node'])}
                            className="mt-8 first:mt-6 font-medium text-2xl tracking-tight scroll-m-20"
                        />
                    );
                },
                h3(props) {
                    return (
                        <h3
                            {...omit(props, ['node'])}
                            className="mt-6 first:mt-0 font-medium text-xl tracking-tight scroll-m-20"
                        />
                    );
                },
                h4(props) {
                    return (
                        <h4
                            {...omit(props, ['node'])}
                            className="mt-6 first:mt-0 font-medium text-lg tracking-tight scroll-m-20"
                        />
                    );
                },
                p(props) {
                    return (
                        <div
                            {...omit(props, ['node'])}
                            className="not-first:mt-2 text-secondary-foreground wrap-break-word leading-7 whitespace-pre-wrap"
                            role="article"
                        />
                    );
                },
                table({ children, ...props }) {
                    return (
                        <TableWithExport tableProps={props}>
                            {children}
                        </TableWithExport>
                    );
                },
                thead({ children, ...props }) {
                    return (
                        <TableHeader
                            {...(props as any)}
                            className="bg-muted/30 font-medium"
                        >
                            {children}
                        </TableHeader>
                    );
                },
                tr({ children, ...props }) {
                    return <TableRow {...(props as any)}>{children}</TableRow>;
                },
                th({ children, ...props }) {
                    return (
                        <TableHead {...(props as any)} className="font-medium">
                            {children}
                        </TableHead>
                    );
                },
                td({ children, ...props }) {
                    return (
                        <TableCell {...(props as any)}>{children}</TableCell>
                    );
                },
                tbody({ children, ...props }) {
                    return (
                        <TableBody {...(props as any)}>{children}</TableBody>
                    );
                },
                // @ts-expect-error custom plugin
                blinkingCursor: () => <BlinkingCursor whitespace />,
                alert: ({
                    type,
                    children,
                    ...props
                }: AlertProps & { type?: string }) => {
                    const alertType = normalizeAlertType(
                        type || props.variant || 'info'
                    );
                    return alertComponents.Alert({
                        variant: alertType,
                        children
                    });
                }
            }}
        >
            {children}
        </ReactMarkdown>
    );
};

export { Markdown };
